import React, { useRef, useEffect } from 'react';

import { X } from 'lucide-react';
import ReactDOM from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title, actions }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [wrapperRef, onClose]);

  useEffect(() => {
    const html = document.querySelector('html');
    if (html) html.style.overflowY = isOpen ? 'hidden' : 'visible';
    return () => { if (html) html.style.overflowY = 'visible'; };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={wrapperRef}
        className="relative flex flex-col max-h-[90vh] w-11/12 max-w-2xl overflow-hidden bg-panel border border-line shadow-2xl"
      >
        {/* Crosshair corners */}
        {[{ top: -5, left: -5 }, { top: -5, right: -5 }, { bottom: -5, left: -5 }, { bottom: -5, right: -5 }].map((pos, i) => (
          <div key={i} style={{ position: 'absolute', width: 9, height: 9, ...pos, zIndex: 2, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: '100%', background: '#92de71', transform: 'translateX(-0.5px)' }} />
            <div style={{ position: 'absolute', top: '50%', left: 0, height: 1, width: '100%', background: '#92de71', transform: 'translateY(-0.5px)' }} />
          </div>
        ))}

        {/* Header */}
        {title && (
          <header className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h3 className="font-['Archivo'] text-[15px] font-bold text-text">{title}</h3>
            <div className="flex items-center gap-2">
              {actions}
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-muted hover:text-text transition-colors cursor-pointer"
                aria-label="Close dialog"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
